import { useEffect, useRef, useState } from "react";
import { JitsiMeeting } from "@jitsi/react-sdk";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setActivePanel, setApiRef, setChatIndication, setIsMeetJoined, setJitsiHostId } from "../redux/slices/JitsiMeetSlice";
import { useAppSelector } from "../redux/app/hooks";
import Loader from "../components/Loader/Loader";
import WarningPopup from "../components/WarningPopup/WarningPopup";
import { STUDENT_ROLE, TEACHER_ROLE, BOT } from "../utils/constants";
import { useMutation } from "@apollo/client";
import { END_THE_LIVE_SESSION, UPDATE_SESSION_PARTICIPANTS_COUNT, UPDATE_SESSION_STATUS } from "../pages/LiveClass/api/LiveClassQuery";
import moment from "moment";
import { CustomIJitsiMeetExternalApi, RecoringType, getConfigOverwrite } from "./JitsiHelper";
import ToasterWithMessage from "../components/toaster";
import { ADD_SESSION_PARTICIPANTS, DELETE_SESSION_PARTICIPANT, UPDATE_SESSION_RECORD } from "../Chime/helpers/Api/ChimeApi";
import { setCurrentSessionRecordingDetail } from "../redux/slices/dashboardSlice";
import axios from "axios";
import ConfirmationPopup from "../components/ConfirmationPopup/ConfirmationPopup";
import images from "../assets/images";

// const jitsiServerURL = process.env.REACT_APP_JITSI_MEET_URL;
// const recordingServerURL = process.env.REACT_APP_JITSI_BOT_URL;

export const JitsiConferenceCustomUI = () => {

    const [openWarningPopup, setOpenWarningPopup] = useState<boolean>(false);
    const [apiLoading, setApiLoading] = useState<boolean>(true);
    const localUserIdRef = useRef<string | null>(null);
    const { state } = useLocation();
    const { meetingId, userName, isHost, isCreateMeeting, hostId, userId, avatarUrl, meetComponent, jitsiUrls } = state ?? {};
    const jitsiServerURL = jitsiUrls?.jitsiMeetUrl;
    const recordingServerURL = `https://${jitsiUrls?.jitsiBotUrl}`;
    const { selectedClassRoomId, role, jitsiRecordingService } = useAppSelector((state) => state?.auth);
    const { currentSessionId, currentSessionDetail, currentSessionRecordingDetail } = useAppSelector((state) => state?.dashboard);
    const selectedSubjectId = localStorage.getItem('selectedSId');
    const timeUntilFinalCallRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const timeUntilToastRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const apiRef = useRef<any>(null);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const userInfo = { email: hostId, displayName: `${isHost ? userName + "-" + hostId : userName }`, avatarURL: avatarUrl };
    const { error } = ToasterWithMessage();
    const [isRecording, setIsRecording] = useState<boolean>(false);
    const [openConfirmationPopup, setOpenConfirmationPopup] = useState<boolean>(false);
    const { Crying } = images;
    const [hasBotJoined, sethasBotJoined] = useState<boolean>(false);
    const [warningMessage, setWarningMessage] = useState<string>("");
    console.log("[Log] jitsiServerURL", { jitsiServerURL, recordingServerURL, jitsiRecordingService});

    // API's for updating the live class start status
    const [updateSessionStatus] = useMutation(UPDATE_SESSION_STATUS, {
      fetchPolicy: "no-cache",
      onCompleted: (data) => {},
      onError: (err) => {
        error(err?.message);
      }
    });
    
    // API's for updating the live class end status
    const [updateSessionEnd] = useMutation(END_THE_LIVE_SESSION, {
        fetchPolicy: "no-cache",
        onCompleted: (data) => {},
        onError: (err) => {
            error(err?.message);
        }
    });

    // API's for updating the live class participant count
    const [updateSessionParticipantCount] = useMutation(UPDATE_SESSION_PARTICIPANTS_COUNT, {
        fetchPolicy: "no-cache",
        onCompleted: (data) => {},
        onError: (err) => {
            error(err?.message);
        }
    });

    // API's for updating the live class record start timer and recording status
    const [updateSessionRecord] = useMutation(UPDATE_SESSION_RECORD, {
        fetchPolicy: "no-cache",
        onCompleted: (data) => {},
        onError: (err) => {
            error(err?.message);
        }
    });

    const handleModerator = async (apiObj: CustomIJitsiMeetExternalApi) => {
        try {
            const roomsInfo = await apiObj.getRoomsInfo();
            const mainRoom = roomsInfo.rooms?.find((room: any) => room.isMainRoom);

            if (mainRoom) {
                const moderator = mainRoom.participants.find((participant: any) => participant.role === "moderator");
                const actualHost = mainRoom.participants.find((participant: any) => participant?.displayName?.includes(hostId));
                console.log("[Log] handleModerator", { roomsInfo, mainRoom, moderator, actualHost, currentUserId: apiObj?._myUserID });
                if (moderator?.id === apiObj?._myUserID && moderator?.id !== actualHost?.id) {
                    apiObj.executeCommand('grantModerator', actualHost?.id);
                }
            }
        } catch (error) {
            console.error("[Log] Error in handleModerator:", error);
        }
    };

    const pinModeratorOrLocalUser  = async (apiObj: CustomIJitsiMeetExternalApi, screenSharingIds: string[]) => {
        try {
            const roomsInfo = await apiObj.getRoomsInfo();
            const mainRoom = roomsInfo.rooms?.find((room: any) => room.isMainRoom);

            if (mainRoom) {
                const moderator = mainRoom.participants.find((participant: any) => participant.role === "moderator");
                console.log("[Log] pinModeratorOrLocalUser", { roomsInfo, mainRoom, moderator, localUserIdRef });
                dispatch(setJitsiHostId({ jitsiHostId: moderator?.id }));
                if (screenSharingIds?.length > 0) {
                    apiObj.executeCommand('setLargeVideoParticipant', screenSharingIds?.[screenSharingIds?.length - 1], 'desktop');
                } else if (moderator) {
                    apiObj.pinParticipant(moderator?.id);
                } else if (localUserIdRef.current) {
                    apiObj.pinParticipant(localUserIdRef?.current);
                }
            }
        } catch (error) {
            console.error("[Log] Error in pinModeratorOrLocalUser:", error);
        }
    };

    const customizeIFrame = (divRef: HTMLDivElement) => {
        const iframeRef = divRef.querySelector("iframe");
        if (iframeRef) {
        iframeRef.style.height = `100vh`;
        iframeRef.style.width = `100%`;
        iframeRef.style.overflow = `hidden`;
        }
    };

    const closeWarnPopup  = () => {
        setOpenWarningPopup(false);
        if (warningMessage === "Unable to join the meeting. Please try again.") {
            navigatePaths();
        }
        setWarningMessage("");
    };

    const updateEndMeeting = async () => {
        if (meetComponent === "liveclass" && selectedClassRoomId && currentSessionId && selectedSubjectId) {
            await updateSessionEnd({
                variables: {
                cId: selectedClassRoomId,
                sId: currentSessionId,
                sjId: selectedSubjectId,
                },
            });
        }
    }

    const handleLeaveOrEndMeetingClick = async (action: "hangup" | "end-meeting", apiObj: CustomIJitsiMeetExternalApi) => {

        console.log("[Log] handleLeaveOrEndMeetingClick", action);
        if ( isHost && isRecording) {
            console.log("[Log] Stopping", { isRecording});
            handleStopRecording(apiObj);
        }
        dispatch(setApiRef({ apiRef: null }));
        dispatch(setActivePanel({ activePanel: null }));
        if (action === "end-meeting") {
            updateEndMeeting();
            if (hasBotJoined && jitsiRecordingService === BOT) {
                setTimeout(() => {
                     removeRecordingBot();
                }, 20000)
            }
        }
        apiObj.executeCommand(action === "end-meeting" ? "endConference" : "hangup");
        dispatch(setIsMeetJoined({ isMeetjoined: false }));
    };

    const navigatePaths = () => {
      console.log("[Log] called navigate paths", { role, meetComponent });
      if (role && meetComponent) {
        if (role === STUDENT_ROLE) {
          navigate("/student-classroom/liveclass", { state: state });
        } else if (role === TEACHER_ROLE) {
          navigate("/teacher-classroom/liveclass", { state: state });
        } 
      }
    };

    const handleUpdateParticipantCount = async (apiObj: CustomIJitsiMeetExternalApi, action?: string) => {
        const roomsInfo = await apiObj.getRoomsInfo();
        const mainRoom = roomsInfo.rooms?.find((room: any) => room.isMainRoom);
        const moderator = mainRoom.participants.find((participant: any) => participant.role === "moderator");
        console.log('[Log] main participants',mainRoom.participants,mainRoom.participants?.length, moderator);        
        console.log("[Log] participant count", { participantCount: apiObj?.getNumberOfParticipants() });
        
        if(mainRoom && (isHost || moderator?.id === apiObj?._myUserID)){
            if (meetComponent === "liveclass" && selectedClassRoomId && currentSessionId && selectedSubjectId) {
                await updateSessionParticipantCount({
                    variables: {
                    cId: selectedClassRoomId,
                    sId: currentSessionId,
                    sjId: selectedSubjectId,
                    participantCount: apiObj?.getNumberOfParticipants()
                    }
                });
            }
        }
    };

    const [addSessionParticipant] = useMutation(ADD_SESSION_PARTICIPANTS, {
  fetchPolicy: "no-cache",
  onCompleted: (data) => {
    console.log("Participant added successfully:", data);
  },
  onError: (err) => {
    error("Error adding participant:");
  },
});

const [deleteSessionParticipant] = useMutation(DELETE_SESSION_PARTICIPANT, {
  fetchPolicy: "no-cache",
  onCompleted: (data) => {
    console.log("Participant deleted successfully:", data);
  },
  onError: (err) => {
    // error("Error deleting participant:");
    console.log(err)
  },
});
    const handleAddParticipant = async (
      apiObj: CustomIJitsiMeetExternalApi
    ) => {
      // To get info about the current user id
      const localParticipantId = apiObj._myUserID;
      const participantId = `${userId}|${localParticipantId}`;
      console.log(participantId, "[debug]participantId");

    //   if (meetComponent === "workshop" && currentWorkshopId) {
    //     // await addParticipantWorkshop({
    //     //   variables: {
    //     //     participantId: participantId,
    //     //     wsId: currentWorkshopId,
    //     //   },
    //     // });
    //   } else if (meetComponent === "pta" && currentPtaMeetId) {
    //     // await addParticipantPTAMeeting({
    //     //   variables: {
    //     //     mId: currentPtaMeetId,
    //     //     participantId: participantId,
    //     //   },
    //     // });
    //   }  
        if (
        meetComponent === "liveclass" &&
        currentSessionId &&
        selectedSubjectId &&
        selectedClassRoomId
      ) {
        addSessionParticipant({
          variables: {
            cId: selectedClassRoomId,
            sId: currentSessionId,
            sjId: selectedSubjectId,
            participantId: participantId,
          },
        });
      }
    };

    const handleDeleteParticipant = async (
      apiObj: CustomIJitsiMeetExternalApi
    ) => {
      // To get info about the current user id
      const localParticipantId = apiObj._myUserID;
      const participantId = `${userId}|${localParticipantId}`
      console.log(participantId, "[debug]participantId")

    //   if (meetComponent === "workshop") {
    //     // await deleteParticipantWorkshop({
    //     //   variables: {
    //     //     participantId: participantId,
    //     //     wsId: currentWorkshopId,
    //     //   },
    //     // });
    //   }
    //   else if (meetComponent === "pta" && currentPtaMeetId) {
    //     // await deleteParticipantPTAMeeting({
    //     //   variables: {
    //     //     mId: currentPtaMeetId,
    //     //     participantId: participantId,
    //     //   },
    //     // });
    //   }
     
        if (
        meetComponent === "liveclass" &&
        currentSessionId &&
        selectedSubjectId &&
        selectedClassRoomId
      ) {
        deleteSessionParticipant({
          variables: {
            cId: selectedClassRoomId,
            sId: currentSessionId,
            sjId: selectedSubjectId,
            participantId: participantId,
          },
        });
      }
    };

    const handleUpdateRecordTime = async (recordingStatus: boolean, recordStartTime: string) => {
        if (meetComponent === "liveclass" && selectedClassRoomId && currentSessionId && selectedSubjectId) {
            await updateSessionRecord({
                variables: {
                    cId: selectedClassRoomId,
                    sId: currentSessionId,
                    sjId: selectedSubjectId,
                    isRecordStart: recordingStatus,
                    recordStarttime: recordStartTime,
                },
            });
        }
        dispatch(setCurrentSessionRecordingDetail({ recordingStartedTime: recordStartTime, recordingStarted: recordingStatus }));
    };

    const handleUpdateMeetingStatus = async () => {
        if (meetComponent === "liveclass" && selectedClassRoomId && selectedSubjectId && currentSessionId && meetingId && hostId) {
            await updateSessionStatus({
                variables: {
                    cId: selectedClassRoomId,
                    sjId: selectedSubjectId,
                    sId: currentSessionId,
                    sCode: meetingId,
                    hostId: hostId,
                }
            });
        }
    };

    const checkEndTime = () => {
        console.log("[Log] check end time method");
        apiRef.current.executeCommand('endConference');
    };

    useEffect(() => {

        console.log("[Log] inside auto useEffect");
        console.log("[Log] Electron", window.electron);
        
        if (window.electron) {
            window.electron.startScreenCapture();
        }

        // Parse start and end times
        const start = moment(currentSessionDetail.startDate);
        const end = moment(currentSessionDetail.endDate);
    
        // Calculate duration in minutes
        const duration = moment.duration(end.diff(start)).asMinutes();
    
        let adjustedEndTime;
    
        if (duration <= 5) {
            adjustedEndTime = end.clone().subtract(1, 'minute');
        } else {
            adjustedEndTime = end.clone().subtract(5, 'minutes');
        }
    
        // Calculate the times for the toaster and final method
        const currentTime = moment();
        const timeUntilToast = adjustedEndTime.diff(currentTime);
        const timeUntilFinalCall = end.clone().add(1, 'minute').diff(currentTime);
    
        // Set timeout for showing toaster
        if (timeUntilToast > 0) {
            timeUntilToastRef.current = setTimeout(() => {
                if (role === TEACHER_ROLE && meetComponent === "liveclass") {
                setOpenWarningPopup(true);
                setWarningMessage('The meeting will be automatically ended on scheduled time');
                }
            }, timeUntilToast);
        }
    
        // Set timeout for calling the final method
        if (timeUntilFinalCall > 0) {
            timeUntilFinalCallRef.current = setTimeout(() => {
            console.log("[Log] inside timeout");
            checkEndTime();
            }, timeUntilFinalCall);
        }

        return () => {
            if(timeUntilToastRef.current){
                clearTimeout(timeUntilToastRef.current);
            }
            if(timeUntilFinalCallRef.current){
                clearTimeout(timeUntilFinalCallRef.current);
            }
        }
    }, [currentSessionDetail?.startDate, currentSessionDetail?.endDate, checkEndTime]);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (apiLoading) {
                console.log("[Log jitsi] Jitsi failed to load. Navigating back...");
                setWarningMessage('Unable to join the meeting. Please try again.');
                setOpenWarningPopup(true);
            }
        }, 60000);
        if (!apiLoading) {
            console.log("[Log jitsi] Meeting loaded successfully");
            setOpenWarningPopup(false);
            setWarningMessage("");
        }
        console.log("[Log jitsi] timeoutId", timeoutId);

        return () => clearTimeout(timeoutId);
    }, [apiLoading]);
    
    const addRecordingBot = async () => {
        if (!apiRef.current) {
            console.error("[Log] Jitsi API not available");
            return;
        }

        try {
            const response = await axios.post(`${recordingServerURL}/joinMeet`, {
                meet_link: `https://${jitsiServerURL}/${meetingId}`,
                start_record: false,
            }, { timeout: 30000 });
            console.log("[Log Bot] Join meet response:", response);
            sethasBotJoined(true);
        } catch (err: any) {
            console.error("[Log Bot] Join meet error:", err);
            sethasBotJoined(false);
        }
    };

    const removeRecordingBot = async () => {
        try {
            const response = await axios.post(`${recordingServerURL}/stopMeet`, {
                meet_link: `https://${jitsiServerURL}/${meetingId}`
            }, { timeout: 30000 });
            console.log("[Log Bot] stop meet response:", response);
            sethasBotJoined(false);
        } catch (err: any) {
            console.error("[Log Bot] stop meet error:", err);
        }
    };

    const getBotRecordStatus = async () => {
        try {
            const response = await axios.post(`${recordingServerURL}/get/record/status`, {
                meet_link: `https://${jitsiServerURL}/${meetingId}`
            }, { timeout: 30000 });
            console.log("[Log Bot] Bot record status:", response);
            return response?.data?.status;
        } catch (err: any) {
            console.error("[Log Bot] Bot record status error:", err);
        }
    }

    const handleStartRecording = async (apiObj: CustomIJitsiMeetExternalApi) => {

        const newRecordingStartTime = new Date().toISOString();
        const recordingData = {
            type: "SET_RECORD_START_TIMER",
            recordStartTime: newRecordingStartTime,
            recordingStatus: true,
            jitsiRecordingService: jitsiRecordingService,
            isHost
        };

        if (jitsiRecordingService === BOT) {
            console.log("[Log Bot] Start recording clicked", { hasBotJoined });
            try {
                await getBotRecordStatus();
                const response = await axios.post(`${recordingServerURL}/startRecording`, {
                    meet_link: `https://${jitsiServerURL}/${meetingId}`,
                    start_record: true
                }, { timeout: 30000 });
                console.log("[Log Bot] Start recording response:", response);
                // apiObj.executeCommand("showNotification", {
                //     title: "custom-layout-action",
                //     description: recordingData
                // });
                // apiObj.executeCommand('sendEndpointTextMessage', '', recordingData);
                apiObj.executeCommand('sendChatMessage', JSON.stringify({ "TYPE": "RECORDING", "MESSAGE": recordingData}));
                handleUpdateRecordTime(true, newRecordingStartTime);
                setIsRecording(true);
            } catch (err: any) {
                error("[Log Bot] Start recording error:", err);
                setIsRecording(false);
            }
        }
        else {
            apiObj.startRecording({ 
                mode: "file",
                dropboxToken: "",
                shouldShare: false,
                rtmpStreamKey: "",
                rtmpBroadcastID: "",
                youtubeStreamKey: "",
                youtubeBroadcastID: "",
            });
            // apiObj.executeCommand("showNotification", {
            //     title: "custom-layout-action",
            //     description: recordingData
            // });
            // apiObj.executeCommand('sendEndpointTextMessage', '', recordingData);
            apiObj.executeCommand('sendChatMessage', JSON.stringify({ "TYPE": "RECORDING", "MESSAGE": recordingData}));
            handleUpdateRecordTime(true, newRecordingStartTime);
            setIsRecording(true);
        }
    };

    const handleStopRecording = async (apiObj: CustomIJitsiMeetExternalApi) => {
        const newRecordingStopTime = new Date().toISOString();
        const recordingData = {
            type: "SET_RECORD_START_TIMER",
            recordStartTime: null,
            recordingStatus: false,
            jitsiRecordingService: jitsiRecordingService,
            isHost
        };
        if (jitsiRecordingService === BOT) {
            console.log("[Log Bot] Stop recording clicked", { hasBotJoined });
            try {
                await getBotRecordStatus();
                const response = await axios.post(`${recordingServerURL}/stopRecording`, {
                    meet_link: `https://${jitsiServerURL}/${meetingId}`,
                }, { timeout: 30000 });
                console.log("[Log Bot] Stop recording response:", response);
                // apiObj.executeCommand('sendEndpointTextMessage', '', recordingData);
                // apiObj.executeCommand("showNotification", {
                //     title: "custom-layout-action",
                //     description: recordingData,
                // });
                apiObj.executeCommand('sendChatMessage', JSON.stringify({ "TYPE": "RECORDING", "MESSAGE": recordingData}));
                handleUpdateRecordTime(false, newRecordingStopTime);
                setIsRecording(false);
            } catch (err: any) {
                console.error("[Log Bot] Stop recording error:", err);
            }
        }
        else {
            apiObj.stopRecording("file");
            // apiObj.executeCommand("showNotification", {
            //     title: "custom-layout-action",
            //     description: recordingData
            // });
            // apiObj.executeCommand('sendEndpointTextMessage', '', recordingData);
            apiObj.executeCommand('sendChatMessage', JSON.stringify({ "TYPE": "RECORDING", "MESSAGE": recordingData}));
            handleUpdateRecordTime(false, newRecordingStopTime);
            setIsRecording(false);
        }
    };


    const handleApiReady = (apiObj: CustomIJitsiMeetExternalApi) => {

        if (apiObj) {
            dispatch(setApiRef({ apiRef: apiObj }));
            apiRef.current = apiObj;
        }
        console.log("[Log] getConfigOverwrite", getConfigOverwrite(isHost));
        
        let screenSharingIds: string[] = [];

        apiObj.on("contentSharingParticipantsChanged", (res) => {
            console.log("[Log] getContentSharingParticipants", res);
            pinModeratorOrLocalUser(apiObj, res?.data);
            screenSharingIds = res?.data;
        });

        apiObj.on("recordingStatusChanged", (val: RecoringType) => {
            console.log("[Log] recordingStatusChanged", val);
        });

        apiObj.on("videoConferenceJoined", async () => {
            console.log("[Log] videoConferenceJoined", { isCreateMeeting, isHost });
            setApiLoading(false);
            dispatch(setIsMeetJoined({ isMeetjoined: true }));
            if (isHost && isCreateMeeting) {
                if ( jitsiRecordingService === BOT) {
                    addRecordingBot();
                }
                handleUpdateMeetingStatus();
            }
            pinModeratorOrLocalUser(apiObj, screenSharingIds);
            handleUpdateParticipantCount(apiObj);
            handleAddParticipant(apiObj)
            // apiRef.current.executeCommand("showNotification", {
            //     title: "custom-layout-action",
            //     description: JSON.stringify({
            //         type: "SET_RECORD_START_TIMER",
            //         recordStartTime: currentSessionRecordingDetail?.recordingStartedTime,
            //         recordingStatus: currentSessionRecordingDetail?.recordingStarted,
            //         jitsiRecordingService: jitsiRecordingService,
            //         isHost
            //     }),
            // });
        });

        apiObj.on("videoConferenceLeft", async() => {
            console.log("[Log] videoConferenceLeft");
            await handleUpdateParticipantCount(apiObj,"videoConferenceLeft");
            await handleLeaveOrEndMeetingClick("hangup", apiObj);
            await handleDeleteParticipant(apiObj)
        });

        apiObj.on("toolbarButtonClicked", (object) => {
            console.log("[Log] toolbarButtonClicked", object);
            if (object.key === 'end-meeting' && isHost) {
                setOpenConfirmationPopup(true);
            }
            else if (object.key === 'hangup-menu') {
                setOpenConfirmationPopup(true);
            }
            else if (object.key === 'hangup') {
                setOpenConfirmationPopup(true);
                // handleUpdateParticipantCount(apiObj,"videoConferenceLeft");
                // handleLeaveOrEndMeetingClick(object.key, apiObj);
            }
            else if (object.key === 'start-recording' && isHost) {
                handleStartRecording(apiObj);
            }
            else if (object.key === 'stop-recording' && isHost) {
                handleStopRecording(apiObj);
            }
            else if (object.key === 'mute-all' && isHost) {
                apiObj.executeCommand('muteEveryone', 'audio');
            }
        });

        apiObj.on('_requestDesktopSources', async (request, callback) => {
            const { options } = request;
            window.electron.getDesktopSources(options)
                .then((sources:any) => callback({ sources }))
                .catch((error:any) => callback({ error }));
        });

        apiObj.on("chatUpdated", (data) => {
            console.log("[Log] chatUpdated", data);
            dispatch(setActivePanel({ activePanel: data?.isOpen ? "chat" : null }));
            if (data?.isOpen === false && data?.unreadCount > 0) {
                dispatch(setChatIndication({ chatIndication: true }));
            }
            else {
                dispatch(setChatIndication({ chatIndication: false }));
            }
        });

        apiObj?.executeCommand("showNotification", {
            title: "custom-layout-action",
            description: JSON.stringify({
                type: "SET_FILMSTRIP_POSITION",
                filmStripPosition: "top"
            })
        });

        apiObj?.executeCommand("showNotification", {
            title: "custom-layout-action",
            description: JSON.stringify({
                type: "SET_SCREENSHARE_FILMSTRIP_POSITION",
                screenShareFilmStripPosition: "bottom"
            })
        });

        apiObj.on("participantJoined", () => {
            // console.log("[Log] participantJoined", apiObj);
            pinModeratorOrLocalUser(apiObj, screenSharingIds);
            handleModerator(apiObj);
            handleUpdateParticipantCount(apiObj);
        });

        apiObj.on("participantLeft", () => {
            console.log("[Log] participantLeft", apiObj);
            handleModerator(apiObj);
            handleUpdateParticipantCount(apiObj);
        });

        apiObj.on("participantRoleChanged", (val: any) => {
            console.log("[Log] participantRoleChanged", val);
            apiObj.executeCommand('displayName', userName);
            pinModeratorOrLocalUser(apiObj, screenSharingIds);
        });

        apiObj.on("tileViewChanged", async (data) => {
            console.log("[Log] tileViewChanged", data);
            dispatch(setActivePanel({ activePanel: data?.enabled ? "tileView" : null }));
            // if (!data?.enabled) {
            //     await pinModeratorOrLocalUser(apiObj, screenSharingIds);
            //     dispatch(setActivePanel({ activePanel: null }));
            // }
            // else {
            //     dispatch(setActivePanel({ activePanel: "tileView" }));
            // }
        });

        apiObj.on("participantsPaneToggled", (data) => {
            console.log("[Log] participantsPaneToggled", data);
            dispatch(setActivePanel({ activePanel: data?.open ? "participants" : null }));
        });

        apiObj.on("endpointTextMessageReceived", (data) => {
            console.log("[Log] endpointTextMessageReceived", data, JSON.parse(data?.data?.eventData?.text));
            // apiObj.executeCommand("showNotification", {
            //     title: "custom-layout-action",
            //     description: data?.data?.eventData?.text
            // });
        });

    };

    const onYesButtonClick = async () => {
        setOpenConfirmationPopup(false);
        handleUpdateParticipantCount(apiRef.current,"videoConferenceLeft");
        handleLeaveOrEndMeetingClick(isHost ?"end-meeting" : "hangup", apiRef.current);
    };

    const onNoButtonClick = () => {
        setOpenConfirmationPopup(false);
    };

    return (
        <div className="jitsiMainContainer"  style={{ height: "100%" }}>
            {apiLoading && <Loader/>}
            <JitsiMeeting
                domain={jitsiServerURL}
                roomName={meetingId}
                configOverwrite={getConfigOverwrite(isHost)?.configOverwrite}
                interfaceConfigOverwrite={getConfigOverwrite(isHost)?.interfaceConfigOverwrite}
                onApiReady={(api) => handleApiReady(api as CustomIJitsiMeetExternalApi)}
                getIFrameRef={customizeIFrame}
                userInfo={userInfo}
                onReadyToClose={() => navigatePaths()}
            />
            <WarningPopup
                message={warningMessage}
                openWarningPopup={ openWarningPopup }
                closeWarnPopup={ closeWarnPopup }
            />
            <ConfirmationPopup
                open={openConfirmationPopup}
                message={`Are you sure you want to ${isHost ? "End" : "Leave"} this Session?`}
                gifData={Crying}
                yesButtonFunctionality={onYesButtonClick}
                noButtonFunctionality={onNoButtonClick}
                messageWidth={'264px'}
            />
        </div>
    );
};